/**
 * Shared Select primitives — types, size constants, and the size context.
 *
 * These live in a dependency-free leaf module (imports only `react`) so that
 * sibling modules (`select.tsx`, `select-item.tsx`, `combobox.tsx`,
 * `multi-select.tsx`) can share them WITHOUT importing values from
 * `select.tsx`. `select.tsx` runs a compound-component assignment at module
 * top level (`_Select.Item = SelectItem`); if a child module imported a value
 * back from `select.tsx`, the resulting import cycle could evaluate that
 * assignment before `SelectItem`/`ComboBox` were initialized, producing a
 * "Cannot access '…' before initialization" (TDZ) crash under certain bundler
 * module orderings (observed in the production Storybook build).
 *
 * Keep this module free of imports from other `select`/`combobox` files.
 */
import { createContext, type FC, type ReactNode } from 'react';

export type SelectItemType = {
  id: string;
  label?: string;
  avatarUrl?: string;
  isDisabled?: boolean;
  /** Inline text rendered next to the label (same line, secondary color). */
  supportingText?: string;
  /** Help text rendered below the label in a smaller, muted font. */
  description?: string;
  /**
   * Overrides the text used for type-ahead and combobox filtering. Display is
   * unchanged — use this to make an item findable by text that isn't shown in
   * its label (e.g. surfacing an option under its section/group name). When
   * omitted, the searchable text is derived from the label (+ supportingText).
   */
  textValue?: string;
  icon?: FC | ReactNode;
};

/**
 * The size scale shared by every field-shaped form control — Select,
 * MultiSelect, ComboBox, NativeSelect, TimeSelect, Input, TextArea and
 * InputGroup. All five steps exist on all of them, so a caller can set the same
 * `size` on any two and get controls of the same height.
 *
 * Fixed-geometry controls (Checkbox, Toggle, Slider) deliberately do NOT use
 * this scale: they have no padding to grow, so they carry their own smaller
 * `sm | md | lg` vocabulary sized by box dimensions instead.
 */
export type SelectSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface CommonProps {
  hint?: string;
  label?: string | ReactNode;
  tooltip?: string;
  size?: SelectSize;
  placeholder?: string;
}

/**
 * Per-size classes for a field-shaped control.
 *
 * `icon` and `text` exist because the box and its contents must scale together.
 * Historically only `root` was shared, and Select/ComboBox/MultiSelect each
 * hardcoded a `size-5` leading icon and `text-md` inner text — so `xs` shrank
 * the padding while leaving a 20px glyph and 16px text inside a 32px control.
 * Consume all four fields rather than hardcoding any of them.
 */
export interface SelectSizeClasses {
  /** Padding + font size for the control's own box. */
  root: string;
  /** Right padding reserved for the ⌘K shortcut affordance. */
  shortcut: string;
  /** Leading icon dimensions, applied directly to the icon element. */
  icon: string;
  /**
   * Same dimensions as `icon`, pre-written as a `*:data-icon:` child variant
   * for controls that style their icon through a descendant selector rather
   * than on the element itself (Select renders its icon as AriaSelectValue
   * children). Spelled out as a literal because Tailwind generates CSS by
   * scanning source for whole class names — a runtime-built
   * `` `*:data-icon:${icon}` `` would compile to nothing.
   */
  iconSlot: string;
  /** Font size for inner text that sits in a child element of the box (an
   * `<input>`, an overlay span) and therefore does not inherit `root`. */
  text: string;
}

/**
 * Every step is responsive: compact on mobile, full size from the `sm`
 * breakpoint (640px) up. `sm:` is the codebase convention for form-control
 * geometry — page layout and prose use `md:`, but a control's padding and its
 * type must move together or the height lands between the two scales.
 *
 * A step owns BOTH its padding and its font-size/line-height, because height is
 * `2 × py + line-height` — splitting them across two owners is what let the same
 * `size` prop mean different heights on different controls. Consumers must
 * apply `root` to the element that actually renders the text, and must not set
 * a competing `text-*` in their own base classes.
 *
 * Resulting heights (mobile → desktop), given `--text-xs/sm/md` line-heights of
 * 18/20/24px in assets/styles/theme.css:
 *
 *   xs  30 → 32     sm  36 → 40     md  40 → 44     lg  44 → 48     xl  48 → 56
 *
 * Desktop values are unchanged from the original single-viewport scale; the
 * mobile column is the addition.
 */
export const sizes: Record<SelectSize, SelectSizeClasses> = {
  xs: {
    root: 'py-1.5 px-2.5 text-xs sm:text-sm',
    shortcut: 'pr-2',
    icon: 'size-4',
    iconSlot: '*:data-icon:size-4',
    text: 'text-xs sm:text-sm',
  },
  sm: {
    root: 'py-2 px-3 text-sm sm:text-md',
    shortcut: 'pr-2.5',
    icon: 'size-5',
    iconSlot: '*:data-icon:size-5',
    text: 'text-sm sm:text-md',
  },
  md: {
    root: 'py-2 px-3 text-md sm:py-2.5 sm:px-3.5',
    shortcut: 'pr-3',
    icon: 'size-5',
    iconSlot: '*:data-icon:size-5',
    text: 'text-md',
  },
  lg: {
    root: 'py-2.5 px-3.5 text-md sm:py-3 sm:px-4',
    shortcut: 'pr-3.5',
    icon: 'size-5',
    iconSlot: '*:data-icon:size-5',
    text: 'text-md',
  },
  xl: {
    root: 'py-3 px-4 text-md sm:py-4 sm:px-5',
    shortcut: 'pr-4',
    icon: 'size-5',
    iconSlot: '*:data-icon:size-5',
    text: 'text-md',
  },
};

export const SelectContext = createContext<{
  size: SelectSize;
}>({
  size: 'sm',
});
